import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { DischargeFormData } from '../../../../types/formData'
import { DischargeFormField } from '../../../../types/formField'
import { DischargeAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Immunizations subsection adapter
 * Handles vaccine/date pairs for various immunizations (13 fields)
 * This follows the MultiColumn pattern: vaccine + date pairs
 */
export class ImmunizationsAdapter extends BaseSubsectionAdapter<DischargeFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<DischargeFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Hepatitis B vaccine/date pair
    const hepB = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HEP_B_HEP_B
    )?.at(0)

    const hepBDate = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HEP_B_DATE
    )?.at(0)

    // Meningococcal vaccine/date pair
    const meningococcal = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_MENINGOCOCCAL_MENINGOCOCCAL
    )?.at(0)

    const meningococcalDate = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_MENINGOCOCCAL_DATE
    )?.at(0)

    // COVID-19 vaccine/date pair
    const covid19 = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_COVID_19_COVID_19
    )?.at(0)

    const covid19Date = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_COVID_19_DATE
    )?.at(0)

    // DTaP vaccine/date pair
    const dtap = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_DTAP_DTAP
    )?.at(0)

    const dtapDate = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_DTAP_DATE
    )?.at(0)

    // HPV vaccine/date pair
    const hpv = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HPV_HPV
    )?.at(0)

    const hpvDate = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HPV_DATE
    )?.at(0)

    // RSV vaccine/date pair
    const rsv = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_RSV_RSV
    )?.at(0)

    const rsvDate = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_RSV_DATE
    )?.at(0)

    // Notes field
    const notes = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_NOTES
    )?.at(0)

    return {
      [DischargeFormField.IMMUNIZATIONS_HEP_B]: hepB,
      [DischargeFormField.IMMUNIZATIONS_HEP_B_DATE]: hepBDate,
      [DischargeFormField.IMMUNIZATIONS_MENINGOCOCCAL]: meningococcal,
      [DischargeFormField.IMMUNIZATIONS_MENINGOCOCCAL_DATE]: meningococcalDate,
      [DischargeFormField.IMMUNIZATIONS_COVID_19]: covid19,
      [DischargeFormField.IMMUNIZATIONS_COVID_19_DATE]: covid19Date,
      [DischargeFormField.IMMUNIZATIONS_DTAP]: dtap,
      [DischargeFormField.IMMUNIZATIONS_DTAP_DATE]: dtapDate,
      [DischargeFormField.IMMUNIZATIONS_HPV]: hpv,
      [DischargeFormField.IMMUNIZATIONS_HPV_DATE]: hpvDate,
      [DischargeFormField.IMMUNIZATIONS_RSV]: rsv,
      [DischargeFormField.IMMUNIZATIONS_RSV_DATE]: rsvDate,
      [DischargeFormField.IMMUNIZATIONS_NOTES]: notes
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(DischargeFormField.IMMUNIZATIONS_HEP_B)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HEP_B_HEP_B,
        value: this.getFormDataField(DischargeFormField.IMMUNIZATIONS_HEP_B)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.IMMUNIZATIONS_HEP_B_DATE)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HEP_B_DATE,
        value: this.getFormDataField(DischargeFormField.IMMUNIZATIONS_HEP_B_DATE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.IMMUNIZATIONS_MENINGOCOCCAL)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_MENINGOCOCCAL_MENINGOCOCCAL,
        value: this.getFormDataField(DischargeFormField.IMMUNIZATIONS_MENINGOCOCCAL)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.IMMUNIZATIONS_MENINGOCOCCAL_DATE)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_MENINGOCOCCAL_DATE,
        value: this.getFormDataField(DischargeFormField.IMMUNIZATIONS_MENINGOCOCCAL_DATE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.IMMUNIZATIONS_COVID_19)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_COVID_19_COVID_19,
        value: this.getFormDataField(DischargeFormField.IMMUNIZATIONS_COVID_19)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.IMMUNIZATIONS_COVID_19_DATE)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_COVID_19_DATE,
        value: this.getFormDataField(DischargeFormField.IMMUNIZATIONS_COVID_19_DATE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.IMMUNIZATIONS_DTAP)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_DTAP_DTAP,
        value: this.getFormDataField(DischargeFormField.IMMUNIZATIONS_DTAP)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.IMMUNIZATIONS_DTAP_DATE)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_DTAP_DATE,
        value: this.getFormDataField(DischargeFormField.IMMUNIZATIONS_DTAP_DATE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.IMMUNIZATIONS_HPV)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HPV_HPV,
        value: this.getFormDataField(DischargeFormField.IMMUNIZATIONS_HPV)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.IMMUNIZATIONS_HPV_DATE)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HPV_DATE,
        value: this.getFormDataField(DischargeFormField.IMMUNIZATIONS_HPV_DATE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.IMMUNIZATIONS_RSV)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_RSV_RSV,
        value: this.getFormDataField(DischargeFormField.IMMUNIZATIONS_RSV)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.IMMUNIZATIONS_RSV_DATE)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_RSV_DATE,
        value: this.getFormDataField(DischargeFormField.IMMUNIZATIONS_RSV_DATE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.IMMUNIZATIONS_NOTES)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_NOTES,
        value: this.getFormDataField(DischargeFormField.IMMUNIZATIONS_NOTES)
      })
    }

    return mutations
  }
}
